import { useEffect, useState } from 'react';

import {
  catalogueLocationCorpus,
  getCatalogueAutocomplete,
  getCatalogueStats,
  searchPublishedDeals,
  type CatalogueAutocompleteItem,
  type CatalogueLocationSuggestion,
  type CatalogueSort,
  type CatalogueStats,
  type CatalogueStatsFilters,
  type CatalogueTypeFilter,
  type DealKind,
  type DealView,
  type DealWithPhotos,
  type SearchPublishedDealsParams,
} from '@/lib/db/deals';

export type CatalogueQueryInput = Omit<SearchPublishedDealsParams, 'pageSize'> & {
  pageSize?: number;
};

const DEFAULT_PAGE_SIZE = 9;

export function mapDealRowToDealView(row: DealWithPhotos): DealView {
  // Sort by position so the gallery / catalogue card consume a deterministic
  // order regardless of the join's row ordering. Defensive copy — never
  // mutate the input.
  const photos = [...row.deal_photos].sort((a, b) => a.position - b.position);
  // Cover = position 0 (Modèle V1.2 §4.3 L367). If admin uploaded photos
  // without ever pinning one to position 0 (shouldn't happen — BO-3 sets it
  // by default, but defensive fallback to the first sorted entry).
  const cover = photos.find((p) => p.position === 0) ?? photos[0] ?? null;
  const status: DealKind =
    row.status === 'sold' ? 'SOLD' : row.type === 'off_plan' ? 'OFF-PLAN' : 'READY';

  const referencePrice = row.reference_price ?? 0;
  const pricePerSqftAed = row.area_sqft > 0 ? Math.round(row.asking_price / row.area_sqft) : 0;
  const benchmarkSqftAed =
    referencePrice > 0 && row.area_sqft > 0 ? Math.round(referencePrice / row.area_sqft) : 0;

  return {
    id: row.id,
    referenceId: row.reference_id,
    slug: row.slug,
    community: row.community,
    zone: row.zone,
    buildingTitle: row.title,
    building: row.building,
    bedrooms: row.bedrooms,
    bathrooms: row.bathrooms ?? 0,
    areaSqft: row.area_sqft,
    askingAed: row.asking_price,
    dldReferenceAed: referencePrice,
    discountPct: row.discount_pct ?? 0,
    // Scope-aware confidence + scope snapshot (ADR-047) — gate the discount
    // wording on the card / sidebar / Home / gallery via `discountDisplayMode`.
    dldConfidence: (row.dld_confidence as DealView['dldConfidence']) ?? null,
    dldScope: row.dld_scope ?? null,
    pricePerSqftAed,
    benchmarkSqftAed,
    status,
    // Raw type drives the off-plan "Original price" wording (vs ready "DLD ref")
    // independently of `status` (which collapses to 'SOLD' once sold).
    dealType: row.type as 'ready' | 'off_plan',
    imageLabel: cover?.alt_text ?? row.title,
    advisorId: row.advisor_id,
    imageUrl: cover?.url,
    photos: photos.map((p) => ({
      url: p.url,
      alt_text: p.alt_text ?? row.title,
      position: p.position,
    })),
    latitude: row.latitude !== null ? Number(row.latitude) : undefined,
    longitude: row.longitude !== null ? Number(row.longitude) : undefined,
    isFeatured: row.is_featured,
    isPick: false,
    floor: row.floor !== null ? String(row.floor) : undefined,
    view: row.view ?? undefined,
    handover: row.handover ?? undefined,
    propertyType: row.property_type,
    publishedAt: row.publication_date ?? row.created_at,
    description: row.description,
  };
}

export type CatalogueQueryState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  deals: DealView[];
  /** Exact count of deals matching the filters (atomic with `deals`). */
  total: number;
  error: Error | null;
};

const INITIAL_QUERY: CatalogueQueryState = {
  status: 'idle',
  deals: [],
  total: 0,
  error: null,
};

// useCatalogueDeals — fetches the requested page whenever any filter /
// sort / page changes. Stale responses are ignored via a per-effect
// "cancelled" flag, so a fast filter swap cannot leave the previous
// request's payload in state.
export function useCatalogueDeals(input: CatalogueQueryInput): CatalogueQueryState {
  const [state, setState] = useState<CatalogueQueryState>(INITIAL_QUERY);

  const {
    typeFilter,
    priceMin,
    priceMax,
    areaMin,
    areaMax,
    zoneKey,
    communityKey,
    buildingKey,
    locationQuery,
    discountMin,
    bedrooms,
    propertyType,
    searchQuery,
    sortBy,
    page,
    pageSize = DEFAULT_PAGE_SIZE,
  } = input;

  // List inputs are joined into a stable string before being added to the
  // dependency array — without this, every render produces a new array
  // identity (e.g. `useState<string[]>([])` returning the same ref but the
  // caller spreading it) and the effect would re-fetch on every render.
  const zoneKeyDep = (zoneKey ?? []).join('|');
  const communityKeyDep = (communityKey ?? []).join('|');
  const buildingKeyDep = (buildingKey ?? []).join('|');
  const bedroomsKey = (bedrooms ?? []).join('|');
  const propertyTypeKey = (propertyType ?? []).join('|');

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    searchPublishedDeals({
      typeFilter,
      priceMin,
      priceMax,
      areaMin,
      areaMax,
      zoneKey,
      communityKey,
      buildingKey,
      locationQuery,
      discountMin,
      bedrooms,
      propertyType,
      searchQuery,
      sortBy,
      page,
      pageSize,
    })
      .then((result) => {
        if (cancelled) return;
        setState({
          status: 'success',
          deals: result.rows.map(mapDealRowToDealView),
          total: result.total,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          deals: [],
          total: 0,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typeFilter,
    priceMin,
    priceMax,
    areaMin,
    areaMax,
    zoneKeyDep,
    communityKeyDep,
    buildingKeyDep,
    locationQuery,
    discountMin,
    bedroomsKey,
    propertyTypeKey,
    searchQuery,
    sortBy,
    page,
    pageSize,
  ]);

  return state;
}

// ─────────────────────────────────────────────────────────────────────────
// Autocomplete + stats hooks
// ─────────────────────────────────────────────────────────────────────────

export type CatalogueAutocompleteState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  items: CatalogueAutocompleteItem[];
  error: Error | null;
};

const INITIAL_AUTOCOMPLETE: CatalogueAutocompleteState = {
  status: 'idle',
  items: [],
  error: null,
};

const AUTOCOMPLETE_HOOK_MIN_LENGTH = 2;

/**
 * `useCatalogueAutocomplete` — fetches title/building suggestions for a
 * search input. The caller is responsible for debouncing the `query`
 * (SearchInput already does so via `onDebouncedChange`); this hook fires
 * one fetch per `query` value change and ignores stale responses.
 */
export function useCatalogueAutocomplete(query: string): CatalogueAutocompleteState {
  const [state, setState] = useState<CatalogueAutocompleteState>(INITIAL_AUTOCOMPLETE);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < AUTOCOMPLETE_HOOK_MIN_LENGTH) {
      setState(INITIAL_AUTOCOMPLETE);
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    getCatalogueAutocomplete(trimmed)
      .then((items) => {
        if (cancelled) return;
        setState({ status: 'success', items, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          items: [],
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  return state;
}

export type CatalogueStatsState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  stats: CatalogueStats | null;
  error: Error | null;
};

const INITIAL_STATS: CatalogueStatsState = {
  status: 'idle',
  stats: null,
  error: null,
};

/**
 * `useCatalogueStats` — fetches header aggregates (count + avg discount +
 * last update) whenever the active filters change. Same dep-array
 * stabilisation trick as `useCatalogueDeals`: list inputs are joined to a
 * stable string before being depended on.
 */
export function useCatalogueStats(filters: CatalogueStatsFilters): CatalogueStatsState {
  const [state, setState] = useState<CatalogueStatsState>(INITIAL_STATS);

  const {
    typeFilter,
    priceMin,
    priceMax,
    areaMin,
    areaMax,
    zoneKey,
    communityKey,
    buildingKey,
    locationQuery,
    discountMin,
    bedrooms,
    propertyType,
    searchQuery,
  } = filters;

  const zoneKeyDep = (zoneKey ?? []).join('|');
  const communityKeyDep = (communityKey ?? []).join('|');
  const buildingKeyDep = (buildingKey ?? []).join('|');
  const bedroomsKey = (bedrooms ?? []).join('|');
  const propertyTypeKey = (propertyType ?? []).join('|');

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    getCatalogueStats({
      typeFilter,
      priceMin,
      priceMax,
      areaMin,
      areaMax,
      zoneKey,
      communityKey,
      buildingKey,
      locationQuery,
      discountMin,
      bedrooms,
      propertyType,
      searchQuery,
    })
      .then((stats) => {
        if (cancelled) return;
        setState({ status: 'success', stats, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          stats: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typeFilter,
    priceMin,
    priceMax,
    areaMin,
    areaMax,
    zoneKeyDep,
    communityKeyDep,
    buildingKeyDep,
    locationQuery,
    discountMin,
    bedroomsKey,
    propertyTypeKey,
    searchQuery,
  ]);

  return state;
}

export type CatalogueLocationCorpusState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  options: CatalogueLocationSuggestion[];
  error: Error | null;
};

const INITIAL_LOCATION_CORPUS: CatalogueLocationCorpusState = {
  status: 'idle',
  options: [],
  error: null,
};

/**
 * Catalogue location autocomplete corpus (addendum ADR-045 §8) — the full set
 * of DISTINCT location text values (zone / community / building) across the
 * published catalogue, fetched once. Building-first, server-ordered. The
 * location field filters this list client-side (the corpus is tiny). Sourced
 * from deal text, so NULL-key deals are represented.
 */
export function useCatalogueLocationCorpus(): CatalogueLocationCorpusState {
  const [state, setState] = useState<CatalogueLocationCorpusState>(INITIAL_LOCATION_CORPUS);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    catalogueLocationCorpus()
      .then((options) => {
        if (cancelled) return;
        setState({ status: 'success', options, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          options: [],
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export type { CatalogueSort, CatalogueTypeFilter };
